import { get, identity, last, partial } from "lodash";
import { isProductionEnvironment, isTestEnvironment } from "./environments";
import * as Sentry from "@sentry/react";
import { SeverityLevel } from "@sentry/react";
import { toJS } from "mobx";

export enum LogLevel {
  DEBUG = 0,
  WARN = 10,
  ERROR = 20,
}

const sentryLevels: Record<LogLevel, SeverityLevel> = {
  [LogLevel.DEBUG]: "debug",
  [LogLevel.WARN]: "warning",
  [LogLevel.ERROR]: "error",
};

function parseLogLevel(s: string | null): LogLevel {
  switch (s) {
    case "0":
      return LogLevel.DEBUG;
    default:
      return LogLevel.WARN;
  }
}

let currentLevel: LogLevel = parseLogLevel(sessionStorage.getItem("logLevel"));

if (import.meta.env.DEV || new URLSearchParams(location.search).has("debug")) {
  setLogLevel(LogLevel.DEBUG);
}

export function setLogLevel(level: LogLevel) {
  currentLevel = level;
  sessionStorage.setItem("logLevel", level.toString());
}

export function isDebugLoggingEnabled() {
  return currentLevel === LogLevel.DEBUG;
}

export function logErrorToSentry(logLevel: LogLevel, ...args: unknown[]) {
  if (isTestEnvironment() || isProductionEnvironment()) {
    const level = sentryLevels[logLevel];
    if (args[0] instanceof Error || typeof args[0] !== "string") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      Sentry.captureException(new Error(args[1] ?? "Unknown error", { cause: args[0] }), {
        extra: { data: args.slice(2), cause: args[0], stack: get(args[0], "stack") },
        level,
      });
    } else if (last(args) instanceof Error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      Sentry.captureException(new Error(args[0] ?? "Unknown error", { cause: last(args) }), {
        extra: { data: args.slice(1, -1) },
        level,
      });
    } else {
      Sentry.captureException(new Error(args.length ? String(args[0]) : "Unknown error"), {
        extra: { data: args.slice(1) },
        level,
      });
    }
  }
}

export function log(level: LogLevel, ...args: unknown[]) {
  if (level < currentLevel) {
    return;
  }
  switch (level) {
    case LogLevel.DEBUG:
      // eslint-disable-next-line no-console
      console.log(...args.map((a) => toJS(a)));
      break;
    case LogLevel.WARN:
    case LogLevel.ERROR:
      // eslint-disable-next-line no-console
      console.error(...args.map((a) => toJS(a)));
      logErrorToSentry(level, ...args.map((a) => toJS(a)));
      break;
  }
}

export const logDebug = partial(log, LogLevel.DEBUG);
export const logWarn = partial(log, LogLevel.WARN);
export const logError = partial(log, LogLevel.ERROR);

export const logGroup = (name: string, ...args: unknown[]) => {
  if (currentLevel === LogLevel.DEBUG) {
    // eslint-disable-next-line no-console
    console.group(name, ...args.filter(identity).map((a) => toJS(a)));
  }
};

export const logGroupCollapsed = (name: string, ...args: unknown[]) => {
  if (currentLevel === LogLevel.DEBUG) {
    // eslint-disable-next-line no-console
    console.groupCollapsed(name, ...args.filter(identity).map((a) => toJS(a)));
  }
};

export const logGroupEnd = () => {
  if (currentLevel === LogLevel.DEBUG) {
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};
